import React, { type ImgHTMLAttributes, memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { COLLECT_IMAGE_ERROR_TRACES } from 'reactApp/appHelpers/featuresHelpers/features/collectImageErrorTraces';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';
import { noop } from 'reactApp/utils/helpers';
import { captureException } from 'reactApp/utils/tracer';

import styles from './ImageCancelable.css';

interface Props extends ImgHTMLAttributes<HTMLImageElement> {
    id?: string;
    showLoader?: boolean;
}

export const preventDragHandler = (event) => {
    // For disable img dragging in FF
    event.preventDefault();
    return false;
};

export const ImageCancelable = memo(({ id, showLoader, onError = noop, onLoad = noop, src, ...restProps }: Props) => {
    const imgRef = useRef<HTMLImageElement | null>(null);
    const [isLoading, setIsLoading] = useState(Boolean(showLoader));

    useLayoutEffect(() => {
        return () => {
            if (!imgRef.current) {
                return;
            }
            imgRef.current.src = '';
            imgRef.current.srcset = '';
            imgRef.current.onload = function () {};
            imgRef.current.onerror = function () {};
        };
    }, [id]);

    useEffect(() => {
        setIsLoading(Boolean(showLoader));
    }, [id, showLoader, setIsLoading]);

    const handleError = useCallback(
        (event: React.SyntheticEvent<HTMLImageElement>) => {
            if (src) {
                setIsLoading(false);
                if (COLLECT_IMAGE_ERROR_TRACES) {
                    captureException(new Error(`Cannot load: ${src}`), { issueKey: 'Failed to load image' });
                }
                onError(event);
            }
        },
        [onError, setIsLoading, src]
    );

    const handleLoad = useCallback(
        (event) => {
            setIsLoading(false);
            onLoad(event);
        },
        [onLoad, setIsLoading]
    );

    return (
        <>
            {(!showLoader || Boolean(src)) && (
                <img
                    key={id}
                    ref={imgRef}
                    src={src}
                    onDragStart={preventDragHandler}
                    onMouseDown={preventDragHandler}
                    onError={handleError}
                    onLoad={handleLoad}
                    {...restProps}
                />
            )}
            {showLoader && isLoading && (
                <div className={styles.spinnerWrapper}>
                    <Spinner />
                </div>
            )}
        </>
    );
});

ImageCancelable.displayName = 'ImageCancelable';
